import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Checkbox } from '@/components/atoms/checkbox';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { SurchargeDisclosure } from '@/components/organisms/payments/SurchargeDisclosure';

import { PayabliPublicCheckout } from '@/components/organisms/payments/PayabliPublicCheckout';
import { PAYMENT_ELEMENT_LAYOUT } from '@/components/organisms/payments/StripePaymentMethodSetupForm';

import { useQueryClient } from '@tanstack/react-query';
import { usePaymentPageData, paymentPageKeys } from '@/hooks/payment/usePaymentPageData';
import { publicPost } from '@/lib/api/publicClient';
import { publicPaymentEndpoints } from '@/lib/api/endpoints';
import { cardServiceFeeNote } from '@/lib/payments/cardServiceFeeNote';
import {
  computePaymentMethodDiscount,
  formatDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';
import { stripeRailPresentation } from '@/lib/payments/stripeRailPresentation';
import { defaultPayabliRail, type PayabliRail } from '@/lib/payments/payabli';
import { isTipOverLimit, MAX_TIP_DOLLARS, TIP_LIMIT_MESSAGE } from '@/lib/payments/tipLimit';
import type { PayabliPublicPageConfig } from '@/types/public';
import type { PaymentPageData } from '@/types/onlineBooking';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';
import { BrandAccentBand, PublicCompanyIdentity } from '@/components/public/PublicCompanyIdentity';

export const Route = createFileRoute('/(public)/payment/$id/$id2/$priceid')({
  component: PaymentPage,
});

// ─── Tip selector ─────────────────────────────────────────────────────────────

const DEFAULT_TIP_PERCENTS = [0, 10, 15, 20, 25];

function TipSelector({
  baseAmount,
  tipPercents,
  initialTipCents,
  tipCents,
  onTipChange,
}: {
  baseAmount: number;
  tipPercents: number[];
  initialTipCents?: number;
  /** The tip last reported through onTipChange — drives the over-cap alert. */
  tipCents: number;
  onTipChange: (tipCents: number) => void;
}) {
  const initialPercent = tipPercents.find(
    (pct) => pct > 0 && Math.round((baseAmount * pct) / 100) === (initialTipCents ?? 0),
  );
  const [selected, setSelected] = useState<number | 'custom'>(() => {
    if ((initialTipCents ?? 0) <= 0) return 0;
    return initialPercent ?? 'custom';
  });
  const [customValue, setCustomValue] = useState(() =>
    initialPercent || !initialTipCents ? '' : (initialTipCents / 100).toFixed(2),
  );

  function handlePercentSelect(pct: number) {
    setSelected(pct);
    setCustomValue('');
    onTipChange(Math.round((baseAmount * pct) / 100));
  }

  function handleCustomChange(val: string) {
    setCustomValue(val);
    setSelected('custom');
    const dollars = parseFloat(val);
    onTipChange(isNaN(dollars) ? 0 : Math.round(dollars * 100));
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Add a Tip (optional)</p>
      <div className="flex gap-2 flex-wrap">
        {tipPercents.map((pct) => (
          <button
            key={pct}
            type="button"
            onClick={() => handlePercentSelect(pct)}
            className={[
              'px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              selected === pct
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border hover:border-primary hover:bg-primary/5',
            ].join(' ')}
          >
            {pct === 0 ? 'No tip' : `${pct}%`}
            {pct > 0 && (
              <span className="ml-1 text-xs opacity-70">
                (${((baseAmount * pct) / 10000).toFixed(2)})
              </span>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setSelected('custom')}
          className={[
            'px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            selected === 'custom'
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border hover:border-primary hover:bg-primary/5',
          ].join(' ')}
        >
          Custom
        </button>
      </div>
      {selected === 'custom' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">$</span>
          <input
            type="number"
            min="0"
            max={MAX_TIP_DOLLARS}
            step="0.01"
            value={customValue}
            onChange={(e) => handleCustomChange(e.target.value)}
            placeholder="0.00"
            className="w-28 rounded-lg border border-border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}
      {/* Shown, never clamped: a percent tip on a large amount can pass the
          cap too, and silently changing the customer's figure is worse. */}
      {isTipOverLimit(tipCents) && (
        <p role="alert" className="text-sm text-destructive">
          {TIP_LIMIT_MESSAGE}
        </p>
      )}
    </div>
  );
}

// ─── Shared money + summary bits ──────────────────────────────────────────────

function formatCents(cents: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function PaymentSuccess() {
  return (
    <div className="text-center space-y-4 py-6">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
      </div>
      <h2 className="text-xl font-bold">Payment Successful!</h2>
      <p className="text-muted-foreground text-sm">Thank you. Your payment has been processed.</p>
    </div>
  );
}

/**
 * A charge the processor accepted but has not settled yet (an ACH rail can do
 * this). Saying "Payment Successful!" here would be a claim we cannot back.
 *
 * This is where the payer's part ends: a bank debit takes days to clear, so the
 * icon must not read as a spinner — a payer watching one waits on a page that
 * has nothing left to do. Matches the invoice and estimate-deposit pages.
 */
function PaymentSubmitted() {
  return (
    <div className="text-center space-y-4 py-6">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center">
          <Clock className="h-8 w-8 text-amber-600" aria-hidden="true" />
        </div>
      </div>
      <h2 className="text-xl font-bold">Payment submitted</h2>
      <p className="text-muted-foreground text-sm">
        Your payment is processing. You&apos;ll get a receipt once it clears — no further action is
        needed.
      </p>
    </div>
  );
}

/**
 * Both rail prices, with the one the Pay button will charge given the weight.
 *
 * The prominent figure used to be the ACH/check price while the button charged
 * the card price — the largest number on the page was not the amount being
 * taken (audit §17). Showing one row per rail keeps the cheaper price visible
 * without letting it stand in for the charge.
 */
function DualRailPrices({
  achOrCheckCents,
  cardCents,
  cardLabel,
  selectedRail,
  currency,
}: {
  achOrCheckCents: number;
  cardCents: number;
  cardLabel: string;
  /** Null until the payer picks a method: neither row is emphasised yet. */
  selectedRail: 'card' | 'ach' | null;
  currency: string;
}) {
  const row = (rail: 'card' | 'ach') =>
    selectedRail === rail
      ? 'flex justify-between font-semibold text-foreground text-base'
      : 'flex justify-between text-muted-foreground';
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/40 p-3 text-sm">
      <div className={row('card')}>
        <span>{cardLabel}</span>
        <span>{formatCents(cardCents, currency)}</span>
      </div>
      <div className={row('ach')}>
        <span>ACH/check price</span>
        <span>{formatCents(achOrCheckCents, currency)}</span>
      </div>
    </div>
  );
}

function AmountSummary({
  baseAmount,
  tipCents,
  cardPriceDifferenceCents,
  achDiscountCents = 0,
  cardOffered,
  achOrCheckOffered,
  creditCardOnly,
  selectedRail = null,
  currency,
}: {
  baseAmount: number;
  tipCents: number;
  cardPriceDifferenceCents: number;
  achDiscountCents?: number;
  cardOffered: boolean;
  achOrCheckOffered: boolean;
  /** Which rail the Pay button will charge, when the payer has chosen one. */
  selectedRail?: 'card' | 'ach' | null;
  /**
   * The difference rides CREDIT cards only and this surface cannot yet tell
   * (Payabli resolves funding at tokenization), so every fee-inclusive figure
   * is labelled a credit-card figure. See `cardServiceFeeNote`.
   */
  creditCardOnly: boolean;
  currency: string;
}) {
  if (achDiscountCents > 0 && achOrCheckOffered) {
    return (
      <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span>Amount due</span>
          <span>{formatCents(baseAmount, currency)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>ACH discount</span>
          <span>−{formatCents(achDiscountCents, currency)}</span>
        </div>
        {tipCents > 0 && (
          <div className="flex justify-between">
            <span>Tip</span>
            <span>{formatCents(tipCents, currency)}</span>
          </div>
        )}
        <Separator />
        <div className="flex justify-between font-semibold">
          <span>{tipCents > 0 ? 'ACH total' : 'ACH price'}</span>
          <span>{formatCents(baseAmount - achDiscountCents + tipCents, currency)}</span>
        </div>
        {cardOffered && (
          <div className="flex justify-between font-semibold">
            <span>{tipCents > 0 ? 'Card total' : 'Card price'}</span>
            <span>{formatCents(baseAmount + tipCents, currency)}</span>
          </div>
        )}
      </div>
    );
  }
  const dualRailPrices = cardPriceDifferenceCents > 0 && cardOffered && achOrCheckOffered;
  const deterministicCardDifference =
    cardPriceDifferenceCents > 0 && cardOffered && !achOrCheckOffered
      ? cardPriceDifferenceCents
      : 0;
  const achOrCheckTotal = baseAmount + tipCents;
  const cardTotal = achOrCheckTotal + cardPriceDifferenceCents;
  const cardPriceLabel = creditCardOnly ? 'Credit card price' : 'Card price';
  const cardTotalLabel = creditCardOnly ? 'Credit card total' : 'Card total';
  return (
    <>
      {dualRailPrices ? (
        <DualRailPrices
          achOrCheckCents={baseAmount}
          cardCents={baseAmount + cardPriceDifferenceCents}
          cardLabel={cardPriceLabel}
          selectedRail={selectedRail}
          currency={currency}
        />
      ) : (
        <div className="rounded-lg bg-muted/40 border border-border p-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {deterministicCardDifference > 0 ? cardPriceLabel : 'Amount due'}
          </span>
          <span className="font-semibold text-lg">
            {formatCents(baseAmount + deterministicCardDifference, currency)}
          </span>
        </div>
      )}
      {tipCents > 0 && (
        <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span>{formatCents(baseAmount, currency)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Tip</span>
            <span>{formatCents(tipCents, currency)}</span>
          </div>
          <Separator className="my-1" />
          {dualRailPrices ? (
            <>
              <div className="flex justify-between font-semibold text-foreground">
                <span>ACH/check total</span>
                <span>{formatCents(achOrCheckTotal, currency)}</span>
              </div>
              <div className="flex justify-between font-semibold text-foreground">
                <span>{cardTotalLabel}</span>
                <span>{formatCents(cardTotal, currency)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between font-semibold text-foreground">
              <span>{deterministicCardDifference > 0 ? cardTotalLabel : 'Total'}</span>
              <span>{formatCents(achOrCheckTotal + deterministicCardDifference, currency)}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── Payabli payment form (embedded component, no Stripe context) ─────────────

/**
 * Payabli's analog of the Stripe form below. Payabli has no PaymentIntent and
 * no hosted redirect: the embedded component captures the card inline and hands
 * back a single-use token, which the server exchanges for a one-time stored
 * method and charges. The browser never sends an amount.
 *
 * The card service fee is disclosed CONDITIONALLY: the embedded component
 * reveals the rail and the card's funding only to the SERVER after
 * tokenization, so the note scopes the difference to credit cards and the
 * card-rail total reads "Pay up to". Sending `surchargeDisclosed: true` lets
 * the backend stamp the fee as pending on the claim; the settle applies it only
 * when the tokenized card reports CREDIT funding, so the charge never exceeds
 * what was disclosed for the rail used.
 */
export function PayabliPaymentForm({
  paymentToken,
  baseAmount,
  currency,
  config,
  tipEnabled,
  tipPercents,
  currentTipCents,
  surcharge,
}: {
  paymentToken: string;
  baseAmount: number;
  currency: string;
  config: PayabliPublicPageConfig;
  tipEnabled?: boolean;
  tipPercents?: number[];
  currentTipCents?: number;
  surcharge?: PaymentPageData['surcharge'];
}) {
  const queryClient = useQueryClient();
  const [tipCents, setTipCents] = useState(currentTipCents ?? 0);
  const [outcome, setOutcome] = useState<'SUCCEEDED' | 'PENDING' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The rail the embedded form is showing. Both are reachable now that the
  // form renders the Card | Bank switch, so the fee, the total on the Pay
  // button and the disclosure attestation all follow the payer's choice
  // instead of assuming a card.
  const [rail, setRail] = useState<PayabliRail>(() =>
    defaultPayabliRail({ card: config.acceptCard, ach: config.acceptACH }),
  );
  const discount = computePaymentMethodDiscount(surcharge, baseAmount, 'ACH');
  const achDiscountCents =
    discount.enabled && config.acceptACH ? (surcharge?.discountCents ?? discount.discountCents) : 0;
  const railFeeCents =
    !discount.enabled && surcharge?.enabled === true && config.acceptCard && rail === 'card'
      ? (surcharge.feeCents ?? 0)
      : 0;
  const totalCents = baseAmount - (rail === 'ach' ? achDiscountCents : 0) + tipCents + railFeeCents;
  const feeText =
    discount.enabled && config.acceptACH
      ? `${formatDiscountPercent(discount.percent)} ACH discount: bank transfer ${formatCents(baseAmount - achDiscountCents + tipCents, currency)}${config.acceptCard ? `; card ${formatCents(baseAmount + tipCents, currency)}` : ''}.`
      : !discount.enabled && surcharge?.enabled === true
        ? cardServiceFeeNote({
            // The exact figure the server will add (cost-of-acceptance capped,
            // invoice-backed) — the disclosure the charge attests to.
            surchargePercent: surcharge.surchargePercent,
            feeAmount: surcharge.feeCents ? formatCents(surcharge.feeCents, currency) : null,
            achOrCheckPrice: (baseAmount + tipCents) / 100,
            cardPriceDifference: surcharge.feeCents == null ? null : surcharge.feeCents / 100,
            cardOffered: config.acceptCard,
            achOffered: config.acceptACH,
            selectedRail: rail,
            // The settle adds the claimed fee only when the tokenized card
            // reports CREDIT, which nothing on this page can know yet.
            creditCardOnly: true,
          })
        : '';
  // A debit, prepaid or BIN-less card is charged base + tip, so on the card
  // rail the fee-inclusive figure is a ceiling — same treatment as the public
  // invoice page.
  const payLabel =
    railFeeCents > 0
      ? `Pay up to ${formatCents(totalCents, currency)}`
      : `Pay ${formatCents(totalCents, currency)}`;
  // Default only when the server sent nothing. An explicit [] means the tenant
  // removed every suggested amount and wants a custom amount only — treating it
  // as "empty, so use the defaults" put 10/15/20/25 back on their pay page.
  const percents = tipPercents ?? DEFAULT_TIP_PERCENTS;
  // The server refuses a tip over the cap; the selector explains why.
  const tipOverLimit = Boolean(tipEnabled) && isTipOverLimit(tipCents);

  if (outcome === 'SUCCEEDED') return <PaymentSuccess />;
  // Transient only: the awaited invalidation in onCharge refreshes the page
  // payload, whose server-derived `attemptSettling` state takes over the
  // rendering (and this form unmounts).
  if (outcome === 'PENDING') return <PaymentSubmitted />;

  return (
    <div className="space-y-5">
      <AmountSummary
        baseAmount={baseAmount}
        tipCents={tipCents}
        cardPriceDifferenceCents={
          !discount.enabled && surcharge?.enabled === true ? (surcharge.feeCents ?? 0) : 0
        }
        achDiscountCents={achDiscountCents}
        cardOffered={config.acceptCard}
        achOrCheckOffered={config.acceptACH}
        creditCardOnly
        selectedRail={rail === 'card' ? 'card' : 'ach'}
        currency={currency}
      />

      {tipEnabled && (
        <TipSelector
          baseAmount={baseAmount}
          tipPercents={percents}
          initialTipCents={currentTipCents}
          tipCents={tipCents}
          onTipChange={setTipCents}
        />
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {feeText ? <p className="text-center text-xs text-muted-foreground">{feeText}</p> : null}

      {/* Not rendered over the cap: the embedded form's `disabled` does not
          block its own submit. */}
      {!tipOverLimit && (
        <PayabliPublicCheckout
          config={config}
          submitLabel={payLabel}
          onRailChange={setRail}
          onCharge={async (temporaryToken) => {
            setErrorMessage(null);
            try {
              const charge = await publicPost<{ status?: string }>(
                publicPaymentEndpoints.payabliCharge(),
                {
                  paymentId: paymentToken,
                  temporaryToken,
                  // Sent for validation only — the server recomputes the total
                  // from the link's own base amount plus this tip.
                  ...(tipCents > 0 ? { tipCents } : {}),
                  // Attest the fee note above was rendered — the backend only
                  // ever adds the card service fee when the page disclosed it.
                  // Never on the bank rail: that payer was shown "no fee".
                  ...(!discount.enabled &&
                  surcharge?.enabled === true &&
                  config.acceptCard &&
                  rail === 'card'
                    ? { surchargeDisclosed: true }
                    : {}),
                },
              );
              // Trust the settled status, not the 200. A charge the processor
              // accepted but has not settled must not read as "paid", and a
              // terminal non-success must surface as an error the payer can act
              // on rather than a green checkmark.
              const status = charge?.status?.toUpperCase();
              if (status === 'SUCCEEDED') {
                setOutcome('SUCCEEDED');
                toast.success('Payment successful. Thank you!');
                // Refresh so the page's own status flips to its completed state.
                await queryClient.invalidateQueries({
                  queryKey: paymentPageKeys.detail(paymentToken),
                });
                return;
              }
              if (status === 'PENDING') {
                // Refresh BEFORE reporting pending: the page derives its
                // settling state (and its poll) from the payload's
                // `attemptSettling`, which this fetch makes current.
                await queryClient.invalidateQueries({
                  queryKey: paymentPageKeys.detail(paymentToken),
                });
                setOutcome('PENDING');
                toast.success('Payment submitted.');
                return;
              }
              throw new Error('Payment failed. Please try a different card.');
            } catch (error) {
              const message =
                error instanceof Error && error.message
                  ? error.message
                  : 'Payment failed. Please try again.';
              setErrorMessage(message);
              toast.error(message);
              // Rethrow so the embedded form re-enables and surfaces its own error.
              throw error;
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Inner payment form (requires Stripe context) ─────────────────────────────

export function PaymentForm({
  paymentId,
  baseAmount,
  currency,
  tipEnabled,
  tipPercents,
  currentTipCents,
  surcharge,
  railsKnown = true,
  cardOnly,
  cardOffered = true,
  achOrCheckOffered = !cardOnly,
  canSavePaymentMethod,
}: {
  paymentId: string;
  baseAmount: number;
  currency: string;
  tipEnabled?: boolean;
  tipPercents?: number[];
  currentTipCents?: number;
  surcharge?: PaymentPageData['surcharge'];
  railsKnown?: boolean;
  cardOnly?: boolean;
  cardOffered?: boolean;
  achOrCheckOffered?: boolean;
  canSavePaymentMethod?: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [tipCents, setTipCents] = useState(currentTipCents ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The selected payment-method type, reported by the Payment Element. Drives
  // the credit-card surcharge: a card selection adds the tenant's fee, ACH does
  // not. Undefined until the element reports its initial selection — on a
  // card-only link card is the only rail, so treat that as card immediately.
  const [selectedMethodType, setSelectedMethodType] = useState<string | undefined>();
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);

  const discount = computePaymentMethodDiscount(surcharge, baseAmount, 'ACH');
  const achDiscountCents =
    discount.enabled && achOrCheckOffered
      ? (surcharge?.discountCents ?? discount.discountCents)
      : 0;
  const totalCents = baseAmount + tipCents;
  const isCardSelected =
    selectedMethodType === 'card' || (selectedMethodType === undefined && Boolean(cardOnly));
  const isBankSelected =
    selectedMethodType === 'us_bank_account' ||
    (selectedMethodType === undefined && railsKnown && !cardOffered && achOrCheckOffered);
  // Render the SERVER's exact fee (pre-tax prorated for invoice-backed links,
  // never assessed on the tip — AF-09). Recomputing `base × percent` in the
  // browser overstates the fee whenever the backing invoice carries tax. The
  // base-only product is kept solely as a fallback for a cached payload that
  // predates `feeCents`.
  const priceDifferenceCents =
    !discount.enabled && surcharge?.enabled
      ? (surcharge.feeCents ?? Math.round((baseAmount * surcharge.surchargePercent) / 100))
      : 0;
  const surchargeCents = isCardSelected ? priceDifferenceCents : 0;
  const grandTotalCents = totalCents + surchargeCents - (isBankSelected ? achDiscountCents : 0);
  const dualRailPrices = priceDifferenceCents > 0 && cardOffered && achOrCheckOffered;
  const deterministicCardDifference =
    priceDifferenceCents > 0 && cardOffered && !achOrCheckOffered ? priceDifferenceCents : 0;
  const confirmedLegacyCardDifference = !railsKnown && isCardSelected ? priceDifferenceCents : 0;
  const displayedCardDifference = deterministicCardDifference + confirmedLegacyCardDifference;
  // Default only when the server sent nothing. An explicit [] means the tenant
  // removed every suggested amount and wants a custom amount only — treating it
  // as "empty, so use the defaults" put 10/15/20/25 back on their pay page.
  const percents = tipPercents ?? DEFAULT_TIP_PERCENTS;
  // The server refuses a tip over the cap; the selector explains why.
  const tipOverLimit = Boolean(tipEnabled) && isTipOverLimit(tipCents);

  async function restoreMethodsAfterFailedSave() {
    if (!elements || (!discount.enabled && (!savePaymentMethod || !isCardSelected))) return;
    try {
      await publicPost(publicPaymentEndpoints.createPaymentIntent(), {
        paymentId,
        tipCents,
        ...(discount.enabled ? {} : { method: 'card' }),
        savePaymentMethod: false,
      });
      const elementUpdate = await elements.fetchUpdates();
      if (elementUpdate.error) {
        // The server already restored every allowed rail. Reloading is the
        // only reliable way to replace a stale card-only Payment Element when
        // Stripe cannot apply that update in place.
        window.location.reload();
        return;
      }
      setSavePaymentMethod(false);
    } catch {
      // Keep the original payment error visible. A reload will fetch the
      // server's current PaymentIntent state if this best-effort restore fails.
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (tipOverLimit) {
      setErrorMessage(TIP_LIMIT_MESSAGE);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Report the rail the customer is paying with so the server applies the
      // surcharge for card only. Leave it unset when the selection is still
      // unknown on a multi-method link — the server then defers the fee until
      // card is explicitly chosen (and always surcharges a card-only link).
      const method = isCardSelected ? 'card' : isBankSelected ? 'ach' : undefined;
      await publicPost(publicPaymentEndpoints.createPaymentIntent(), {
        paymentId,
        tipCents,
        savePaymentMethod: savePaymentMethod && isCardSelected,
        ...(method ? { method } : {}),
      });
      const elementUpdate = await elements.fetchUpdates();
      if (elementUpdate.error) {
        throw new Error(elementUpdate.error.message);
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/receipt`,
        },
        redirect: 'if_required',
      });

      if (error) {
        await restoreMethodsAfterFailedSave();
        setErrorMessage(error.message ?? 'Payment failed. Please try again.');
        toast.error(error.message ?? 'Payment failed.');
      } else if (paymentIntent?.status === 'processing') {
        setSubmitted(true);
      } else {
        setSucceeded(true);
      }
    } catch {
      await restoreMethodsAfterFailedSave();
      setErrorMessage('Payment failed. Please try again.');
      toast.error('Payment failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (succeeded) return <PaymentSuccess />;
  if (submitted) return <PaymentSubmitted />;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {discount.enabled ? (
        <AmountSummary
          baseAmount={baseAmount}
          tipCents={tipCents}
          cardPriceDifferenceCents={0}
          achDiscountCents={achDiscountCents}
          cardOffered={cardOffered}
          achOrCheckOffered={achOrCheckOffered}
          creditCardOnly={false}
          currency={currency}
        />
      ) : dualRailPrices ? (
        <DualRailPrices
          achOrCheckCents={baseAmount}
          cardCents={baseAmount + priceDifferenceCents}
          cardLabel="Card price"
          selectedRail={isCardSelected ? 'card' : isBankSelected ? 'ach' : null}
          currency={currency}
        />
      ) : (
        <div className="rounded-lg bg-muted/40 border border-border p-3 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {displayedCardDifference > 0 ? 'Card price' : 'Amount due'}
          </span>
          <span className="font-semibold text-lg">
            {formatCents(baseAmount + displayedCardDifference, currency)}
          </span>
        </div>
      )}

      {tipEnabled && (
        <>
          <TipSelector
            baseAmount={baseAmount}
            tipPercents={percents}
            initialTipCents={currentTipCents}
            tipCents={tipCents}
            onTipChange={setTipCents}
          />
          {tipCents > 0 && !discount.enabled && (
            <div className="rounded-lg bg-muted/40 border border-border p-3 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency.toUpperCase(),
                  }).format(baseAmount / 100)}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tip</span>
                <span>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency.toUpperCase(),
                  }).format(tipCents / 100)}
                </span>
              </div>
              <Separator className="my-1" />
              {dualRailPrices ? (
                <>
                  <div className="flex justify-between font-semibold text-foreground">
                    <span>ACH/check total</span>
                    <span>{formatCents(totalCents, currency)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-foreground">
                    <span>Card total</span>
                    <span>{formatCents(totalCents + priceDifferenceCents, currency)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between font-semibold text-foreground">
                  <span>Total</span>
                  <span>{formatCents(totalCents + displayedCardDifference, currency)}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Link off: it offers its own bank and Klarna options that a card-only
          business never turned on, and a card saved through Link comes back
          as an opaque placeholder that can't be charged again. */}
      <PaymentElement
        options={{
          layout: PAYMENT_ELEMENT_LAYOUT,
          wallets: { link: 'never' },
          readOnly: isSubmitting,
        }}
        onChange={(event) => {
          const methodType = event.value.type;
          setSelectedMethodType(methodType);
          if (methodType !== 'card') {
            setSavePaymentMethod(false);
          }
        }}
      />

      {canSavePaymentMethod && isCardSelected && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
          <Checkbox
            id="save-payment-method"
            checked={savePaymentMethod}
            disabled={isSubmitting}
            onCheckedChange={(checked) => setSavePaymentMethod(checked === true)}
          />
          <label htmlFor="save-payment-method" className="cursor-pointer text-sm leading-5">
            <span className="font-medium">Save this payment method for next time</span>
            <span className="block text-xs text-muted-foreground">
              Optional. Leave this unchecked to use it only for this payment.
            </span>
          </label>
        </div>
      )}

      <SurchargeDisclosure
        baseAmount={totalCents / 100}
        surchargeAmount={priceDifferenceCents / 100}
        enabled={Boolean(!discount.enabled && surcharge?.enabled && railsKnown && cardOffered)}
        achOrCheckOffered={achOrCheckOffered}
        currency={currency.toUpperCase()}
      />

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={!stripe || isSubmitting || tipOverLimit}>
        {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Pay{' '}
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency.toUpperCase(),
        }).format(grandTotalCents / 100)}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Secured by <span className="font-semibold">Stripe</span>
      </p>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PaymentPage() {
  const { id: paymentId } = Route.useParams();
  // An inline Payabli ACH charge that reached the provider is settled only by
  // a webhook. The state is SERVER-derived (`attemptSettling`; the charge
  // handler invalidates this query before reporting PENDING, so the payload
  // is always fresh): a reload lands back on "payment submitted", the page
  // polls until the row goes terminal, and a FAILED settlement renders a
  // retryable failure banner over the pay form.
  const { data, isLoading, isError } = usePaymentPageData(paymentId, {
    refetchInterval: (query) => {
      const current = query.state.data as PaymentPageData | undefined;
      const settling =
        current?.attemptSettling === true && current?.status?.toUpperCase() === 'PENDING';
      return settling ? 4000 : false;
    },
  });
  const pageStatus = data?.status?.toUpperCase();
  const achSettling = data?.attemptSettling === true && pageStatus === 'PENDING';
  // Only a failure that follows an observed settlement (this tab watched the
  // ACH attempt) gets the banner — a cold load of a FAILED link is simply a
  // retryable link.
  const sawSettlingRef = useRef(false);
  if (achSettling) sawSettlingRef.current = true;
  const achFailed = pageStatus === 'FAILED' && sawSettlingRef.current && !achSettling;
  const stripePromise = useMemo(() => {
    // Never load Stripe for a Payabli tenant. Without this guard the
    // VITE_STRIPE_PUBLISHABLE_KEY fallback would happily initialise Stripe on a
    // page that pays through Payabli.
    if (data?.processor === 'payabli') return null;
    const publishableKey = data?.publishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) return null;
    return loadStripe(
      publishableKey,
      data?.stripeAccountId ? { stripeAccount: data.stripeAccountId } : undefined,
    );
  }, [data?.processor, data?.publishableKey, data?.stripeAccountId]);

  if (isLoading) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md">
          <CardHeader>
            <Skeleton className="h-7 w-40" />
            <Skeleton className="h-4 w-56 mt-1" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </PublicCenteredPage>
    );
  }

  if (isError || !data) {
    return (
      <PublicCenteredPage>
        <Card className="w-full max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-lg font-semibold">Payment link not found</h2>
          <p className="text-muted-foreground text-sm mt-2">
            This payment link may be expired or invalid.
          </p>
        </Card>
      </PublicCenteredPage>
    );
  }

  const paymentStatus = data.status?.toUpperCase();
  const isCompleted = paymentStatus === 'SUCCEEDED' || paymentStatus === 'CHARGEBACK_WON';
  const isJobPaidInFull = data.unavailableReason === 'PAID_IN_FULL';
  const exceedsJobBalance = data.unavailableReason === 'AMOUNT_EXCEEDS_BALANCE';
  const isDepositCovered = data.unavailableReason === 'DEPOSIT_COVERED';
  const isUnavailable = data.isPayable === false && !isCompleted;
  // The request itself is still collectible (PENDING/FAILED) but the business
  // can't take payment online right now — usually because Stripe isn't fully
  // connected. Show a "contact the business" state rather than implying the
  // link expired.
  const isPaymentSetupPending =
    isUnavailable && (paymentStatus === 'PENDING' || paymentStatus === 'FAILED');

  return (
    <PublicCenteredPage>
      <Card
        className={
          data.companyPrimaryColor ? 'w-full max-w-md overflow-hidden pt-0' : 'w-full max-w-md'
        }
      >
        <BrandAccentBand color={data.companyPrimaryColor} />
        <CardHeader className="space-y-3">
          {data.companyName ? (
            <PublicCompanyIdentity company={data} eyebrow="Payment requested by" />
          ) : null}
          <CardTitle>Complete Payment</CardTitle>
          {data.description && <CardDescription>{data.description}</CardDescription>}
        </CardHeader>
        <CardContent>
          {isCompleted ? (
            <div className="text-center space-y-4 py-6">
              <div className="flex justify-center">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold">Payment already completed</h2>
              <p className="text-muted-foreground text-sm">
                This payment request has already been paid.
              </p>
            </div>
          ) : isJobPaidInFull ? (
            <div className="text-center space-y-3 py-8">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <h2 className="text-base font-semibold">Job already paid in full</h2>
              <p className="text-muted-foreground text-sm">
                No additional payment is due for this job.
              </p>
            </div>
          ) : isDepositCovered && data.depositClearing ? (
            // Covered only by a bank debit still clearing: on its way, not
            // paid — it can still be returned, so "already paid" overstated it.
            <div className="text-center space-y-3 py-8">
              <Clock className="h-10 w-10 text-amber-600 mx-auto" aria-hidden="true" />
              <h2 className="text-base font-semibold">Deposit payment processing</h2>
              <p className="text-muted-foreground text-sm">
                Your bank payment is on its way. It usually clears in a few business days.
              </p>
            </div>
          ) : isDepositCovered ? (
            <div className="text-center space-y-3 py-8">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <h2 className="text-base font-semibold">Deposit already paid</h2>
              <p className="text-muted-foreground text-sm">
                The deposit for this job has been paid, so this link is no longer needed.
              </p>
            </div>
          ) : exceedsJobBalance ? (
            <div className="text-center space-y-3 py-8">
              <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
              <h2 className="text-base font-semibold">Payment request no longer valid</h2>
              <p className="text-muted-foreground text-sm">
                The remaining job balance changed after this link was created. Please request a new
                payment link.
              </p>
            </div>
          ) : achSettling ? (
            // Server-derived: an ACH charge reached the provider and is
            // still clearing. Never show a live pay form over money in
            // flight — the poll above resolves this to completed or failed.
            <PaymentSubmitted />
          ) : isPaymentSetupPending ? (
            <div className="text-center space-y-3 py-8">
              <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
              <h2 className="text-base font-semibold">Online payment unavailable</h2>
              <p className="text-muted-foreground text-sm">
                {data.companyName ?? 'This business'} isn&apos;t able to accept online payments
                right now. Please contact them directly to settle this payment.
              </p>
            </div>
          ) : isUnavailable ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              This payment request is no longer available.
            </div>
          ) : (
            // Branch on the processor EXPLICITLY. An unknown or missing
            // processor must never fall through to Stripe — a Payabli tenant
            // rendering Stripe Elements would show a dead form.
            (() => {
              const achFailureBanner = achFailed ? (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Your bank transfer could not be completed. No payment was collected — you can
                    try again below with another payment method.
                  </span>
                </div>
              ) : null;
              if (data.processor === 'payabli') {
                return data.payabli ? (
                  <>
                    {achFailureBanner}
                    <PayabliPaymentForm
                      paymentToken={paymentId}
                      baseAmount={data.amount}
                      currency={data.currency}
                      config={data.payabli}
                      tipEnabled={data.tipEnabled}
                      tipPercents={data.tipPercents}
                      currentTipCents={data.currentTipCents}
                      surcharge={data.surcharge}
                    />
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Payment session unavailable.
                  </div>
                );
              }
              // `processor` is absent on a payload cached before this field
              // existed; Stripe was the only option then, so treat it as
              // Stripe rather than breaking a link already in someone's inbox.
              if ((!data.processor || data.processor === 'stripe') && data.clientSecret) {
                const stripeRails = stripeRailPresentation(data.acceptedMethods);
                return (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: data.clientSecret,
                      appearance: { theme: 'stripe' },
                    }}
                  >
                    <PaymentForm
                      paymentId={paymentId}
                      baseAmount={data.amount}
                      currency={data.currency}
                      tipEnabled={data.tipEnabled}
                      tipPercents={data.tipPercents}
                      currentTipCents={data.currentTipCents}
                      surcharge={data.surcharge}
                      {...stripeRails}
                      canSavePaymentMethod={data.canSavePaymentMethod}
                    />
                  </Elements>
                );
              }
              return (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Payment session unavailable.
                </div>
              );
            })()
          )}
        </CardContent>
      </Card>
    </PublicCenteredPage>
  );
}
